import {LoadingContainer} from '@aboutyou/legacy-desktop/src/app/components/Loading';
import TextLink from '@aboutyou/legacy-desktop/src/app/components/TextLink';
import {readIdFromAysaSlug} from '@aboutyou/legacy-desktop/src/app/helpers/aysaSlugs';
import {exists} from '@aboutyou/legacy-desktop/src/app/helpers/exists';
import {getGenderUrl} from '@aboutyou/legacy-desktop/src/app/helpers/gender';
import {useTranslation} from '@aboutyou/legacy-desktop/src/app/helpers/hooks/useTranslation';
import {PageTrackingContextProvider} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/PageTrackingContext';
import {DeferredSectionsTrackingContextProvider} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/SectionTrackingContext';
import {useTrackOriginClick} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/hooks/trackOriginClick';
import {useTrackFeature} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/hooks/useTrackFeature';
import {useTrackingPageUrl} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/hooks/useTrackingPageUrl';
import DocumentHead from '@aboutyou/legacy-desktop/src/app/pages/OutfitDetail/DocumentHead';
import redialHandle from '@aboutyou/legacy-desktop/src/app/pages/OutfitDetail/redial';
import OdpOutfitReco from '@aboutyou/legacy-desktop/src/app/pages/OutfitDetail/sections/OdpOutfitReco';
import OdpStoryReco from '@aboutyou/legacy-desktop/src/app/pages/OutfitDetail/sections/OdpStoryReco';
import {useService} from '@aboutyou/legacy-desktop/src/app/services/utils/context';
import OutfitRecoSlider from '@aboutyou/legacy-desktop/src/app/sharedComponents/OutfitRecoSlider/OutfitRecoSlider';
import ProductTileCTAToBasket from '@aboutyou/legacy-desktop/src/app/sharedComponents/ProductTileConnected/ProductTileCTAToBasket';
import {
  useLocaleStore,
  useOdpStore,
  useRecommendationsStore,
} from '@aboutyou/legacy-desktop/src/app/store/context';
import Outfit from '@aboutyou/legacy-desktop/src/app/store/entities/Outfit/Outfit.store';
import {GenderType} from '@aboutyou/legacy-desktop/src/app/store/features/user/types';
import {HeadingM} from '@aboutyou/legacy-desktop/src/app/uiComponents/atoms/Text/Headings';
import {darkGrayColor} from '@aboutyou/legacy-desktop/src/app/uiComponents/constants/colors';
import {fwMediumBold} from '@aboutyou/legacy-desktop/src/app/uiComponents/constants/typography';
import {
  RecoSectionContent,
  TrackingPageTypeWithId,
} from '@aboutyou/legacy-tracking-sdk';
import {
  ProductMiniAdpWithBasketFlyout,
  SizeSelectionOpenedStateKey,
  useMiniAdpDialogOpenedState,
} from '@aboutyou/logic-components';
import {useObserver} from 'mobx-react-lite';
import React, {StrictMode} from 'react';
import redial from 'redial';
import styled from 'styled-components';
import {withLegacyProviders} from '../LegacyComponentWrapper';

const Container = styled.div`
  width: 100%;
`;

const StyledTextLink = styled(TextLink)`
  ${fwMediumBold};
  color: ${darkGrayColor};
`;

const slideCounts = {
  slideCount: 2,
  slideCountMedium: 3,
  slideCountLarge: 4,
};

const useOutfitsUrl = (outfit: Outfit | null) => {
  const {locale} = useLocaleStore();

  if (!outfit) {
    return '';
  }

  return `/${getGenderUrl(locale)(outfit.gender as GenderType)}/outfits`;
};

const removeDuplicateIds = (ids: string[], withoutIds: Set<string>) => {
  return ids.filter(id => !withoutIds.has(id));
};

const LegacyPage: React.FC<{
  openProductMiniADPDialog: ({
    productId,
    filteredSizeId,
  }: {
    productId: number;
    filteredSizeId?: number;
  }) => void;
}> = ({openProductMiniADPDialog}) => {
  const {t} = useTranslation();
  const outfitId = useOdpStore(store => store.outfitId);
  const primeOutfitId = useOdpStore(store => store.primeOutfitId);
  const idolOutfitIds = useOdpStore(store => store.idolOutfitIds);
  const storyId = useOdpStore(store => store.storyId);
  const isOdpLoading = useOdpStore(store => store.loading);

  const service = useService();
  const outfit = exists(outfitId) ? service.outfits.getById(outfitId) : null;
  const primeOutfit = exists(primeOutfitId)
    ? service.outfits.getById(primeOutfitId)
    : null;
  const story = exists(storyId) ? service.stories.getById(storyId) : null;

  const outfitsURL = useOutfitsUrl(outfit);
  const recommendationsStore = useRecommendationsStore();

  const mainIds = new Set(
    [outfit ? outfit.id : null, primeOutfit ? primeOutfit.id : null].filter(
      exists,
    ),
  );

  const trendingOutfitIds = useObserver(
    () => recommendationsStore.trendingOutfitsIds,
  );
  const trendingOutfitIdsWithoutMainOutfitIds = removeDuplicateIds(
    trendingOutfitIds,
    mainIds,
  );

  const idolOutfitIdsWithoutMainOutfitIds = removeDuplicateIds(
    idolOutfitIds,
    mainIds,
  );

  const trackFeature = useTrackFeature();
  const trackOriginClick = useTrackOriginClick();

  if (!outfit) {
    return <LoadingContainer />;
  }

  const handleProductCtaClick = (productId: number) => {
    trackFeature('odp_direct_add_to_basket', `${outfit.id}|${productId}`, null);
    openProductMiniADPDialog({productId});
  };
  return (
    <Container>
      <DocumentHead isOdpLoading={isOdpLoading} />
      <ProductTileCTAToBasket onCTAClick={handleProductCtaClick}>
        <OdpOutfitReco mainOutfit outfit={outfit} />

        <OutfitRecoSlider
          pullStart
          pullEnd
          outfitIds={idolOutfitIdsWithoutMainOutfitIds}
          slideCounts={slideCounts}
          recoType={RecoSectionContent.fittingProducts}
          dataTestId="IdolOutfitSlider"
          title={
            <HeadingM data-testid="HeadlineText">
              {t('Mehr Outfits von {{idolName}}', {
                idolName: outfit.idol && outfit.idol.name,
              })}
            </HeadingM>
          }
          headerContent={
            <StyledTextLink to={outfitsURL} text={t('Alle Outfits')} />
          }
          onClick={() => {
            trackOriginClick('outfits_same_profile_reco');
          }}
          pageType={null}
        />

        <OdpOutfitReco shifted outfit={primeOutfit} />
      </ProductTileCTAToBasket>

      <OdpStoryReco story={story} />

      <OutfitRecoSlider
        pullStart
        pullEnd
        outfitIds={trendingOutfitIdsWithoutMainOutfitIds}
        slideCounts={slideCounts}
        recoType={RecoSectionContent.trendingOutfits}
        dataTestId="TrendingOutfitsSlider"
        title={
          <HeadingM data-testid="HeadlineText">
            {t('Trending Outfits')}
          </HeadingM>
        }
        headerContent={
          <StyledTextLink to={outfitsURL} text={t('Alle anzeigen')} />
        }
        onClick={() => {
          trackOriginClick('trending_outfits_reco');
        }}
        pageType={null}
      />
    </Container>
  );
};

const TrackedOutfitDetailPage: React.FC<{
  openProductMiniADPDialog: ({
    productId,
    filteredSizeId,
  }: {
    productId: number;
    filteredSizeId?: number;
  }) => void;
}> = ({openProductMiniADPDialog}) => {
  const {path, search} = useTrackingPageUrl();

  const outfitId = readIdFromAysaSlug(path);
  const isOdpLoading = useOdpStore(store => store.loading);

  return (
    <PageTrackingContextProvider
      disabled={isOdpLoading}
      id={outfitId}
      path={path}
      search={search}
      type={TrackingPageTypeWithId.ODP}
    >
      <DeferredSectionsTrackingContextProvider
        key={isOdpLoading ? 'loading' : outfitId}
        sectionCount={9}
      >
        <LegacyPage openProductMiniADPDialog={openProductMiniADPDialog} />
      </DeferredSectionsTrackingContextProvider>
    </PageTrackingContextProvider>
  );
};

const PageWithRedial = redial.provideHooks(redialHandle)(
  TrackedOutfitDetailPage,
);

const OutfitDetailPageWithLegacyProviders = withLegacyProviders(PageWithRedial);

export const OutfitDetailPage: React.FC = () => {
  const {open: openProductMiniADPDialog} = useMiniAdpDialogOpenedState();

  return (
    <>
      <OutfitDetailPageWithLegacyProviders
        openProductMiniADPDialog={openProductMiniADPDialog}
      />

      <StrictMode>
        <ProductMiniAdpWithBasketFlyout
          showAddToWishlistButton
          sizeSelectionStateManagerKey={
            SizeSelectionOpenedStateKey.MAIN_SIZE_SELECTION
          }
        />
      </StrictMode>
    </>
  );
};
